import { useState, useEffect } from 'react';
import apiRequest from '../../../utils/request';

interface Suggestion {
    place_id: string;
    description: string;
}

interface CountryAutoSuggestProps {
    setQuery: (query: string) => void;
    query: string;
    placeholder?: string;
    className?: string;
    title?: string;
    required?: boolean;
}

const CountryAutoSuggest: React.FC<CountryAutoSuggestProps> = ({
    setQuery,
    query,
    placeholder = 'Enter a location',
    className = '',
    title,
    required = false,
}) => {
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isFocused && query.length > 2) {
            const fetchSuggestions = async () => {
                setIsLoading(true);
                try {
                    const response = await apiRequest.request<{
                        statusCode: number;
                        response?: { data?: { predictions?: Suggestion[] } };
                    }>({
                        method: 'post',
                        url: '/settings/get-locations',
                        data: {
                            address: query,
                        },
                        headers: {
                            Authorization: 'Bearer G@V3l2023-new-podus-v001233-SyS',
                        },
                    });
                    setSuggestions(response?.response?.data?.predictions || []);
                } catch (error) {
                    console.error('Error fetching location suggestions:', error);
                    setSuggestions([]);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchSuggestions();
        } else {
            setSuggestions([]);
        }
    }, [query, isFocused]);

    return (
        <div className={`relative w-full ${className}`}>
            {title && (
                <label className="block mb-1 text-secondary font-semibold">
                    {title}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            )}
            <input
                type="text"
                className="w-full px-4 py-3 border border-[#D2D2D2] rounded-lg focus:ring-2 focus:ring-[#151515] focus:border-[#151515] outline-none transition-colors bg-gray-50 focus:bg-white placeholder-[#52525B] text-[14px]"
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setTimeout(() => setIsFocused(false), 120)}
            />
            {isLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#FF6600]"></div>
                </div>
            )}
            {isFocused && suggestions.length > 0 && (
                <ul className="absolute z-100 w-full bg-white border border-[#D2D2D2] rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                    {suggestions.map((suggestion) => (
                        <li
                            key={suggestion.place_id}
                            className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-[14px]"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setQuery(suggestion.description);
                                setSuggestions([]);
                                setIsFocused(false);
                            }}
                        >
                            {suggestion.description}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CountryAutoSuggest;
